import React, { useState, CSSProperties } from 'react';
import { Helmet } from "react-helmet";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import PropertyNavigationBar from './components/PropertyNavigationBar';
import useThemeStore from '../store/themeStore';
import useTokenStore from '../store/store';
import { baseUrl } from '../utils';

interface RewardItem {
  service: string;
  reward: string;
}

interface FormData {
  "Your Name": string;
  "Referral First Name": string;
  "Referral Last Name": string;
  "Referral Type": string;
  "Referral Phone Number": string;
  "Referral Email Address": string;
  "Additional Info About Your Referral (Optional)": string;
  "Website Referred From": string;
}

const ReferralPage: React.FC = () => {
  const theme = useThemeStore((state) => state.theme);
  const getToken = useTokenStore((state) => state.token);
  const [formData, setFormData] = useState<FormData>({
    "Your Name": "",
    "Referral First Name": "",
    "Referral Last Name": "",
    "Referral Type": "",
    "Referral Phone Number": "",
    "Referral Email Address": "",
    "Additional Info About Your Referral (Optional)": "",
    "Website Referred From": theme?.name || "Whitelabel Website"
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setErrorMessage('');

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authentication token if available (same logic as Property.tsx)
    if (getToken && typeof getToken === 'string') {
      const cleanToken = (getToken as string).replace("B9I9N9456", "");
      headers['x-auth-token'] = cleanToken;
      headers['Authorization'] = `Bearer ${cleanToken}`;
      console.log('🔐 Including auth token for authenticated user');
    } else {
      console.log('👤 Submitting as anonymous user');
    }

    try {
      const response = await fetch(`${baseUrl}/whitelabel/referrals`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to submit form');
      }

      setSubmitStatus('success');
      setFormData({
        "Your Name": "",
        "Referral First Name": "",
        "Referral Last Name": "",
        "Referral Type": "",
        "Referral Phone Number": "",
        "Referral Email Address": "",
        "Additional Info About Your Referral (Optional)": "",
        "Website Referred From": theme?.name || "Whitelabel Website"
      });
    } catch (error) {
      console.error('❌ Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const rewardItems: RewardItem[] = [
    { service: 'Listings', reward: '20%–50% of the listing fee' },
    { service: 'Sales', reward: '20%–50% of the sourcing fee' },
    { service: 'Brokerage', reward: '20% of the total fee' },
    { service: 'Legals', reward: '£100–£200 per referral' },
    { service: 'Lettings', reward: '2 months management fee' },
    { service: 'Refurbishments', reward: '2.5% of the total job value' }
  ];

  const isWhiteOrShade = (color: string | undefined): boolean => {
    if (!color) return false;
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  const getThemeColor = (color: string | undefined, fallback: string = 'black'): string => {
    return color || fallback;
  };

  return (
    <div className="ReferralPage bg-primarycolor pt-28" style={{ backgroundColor: "white" }}>
      <Helmet>
        <title>Refer & Earn</title>
        <meta name="description" content="Refer clients and earn rewards with My Property Group" />
      </Helmet>

      <PropertyNavigationBar currentPage="referral" />

      <div className="relative w-full" >
        <div className="max-w-7xl mx-auto px-4 p-4 sm:px-6 lg:px-8 mb-5">
          <div className="text-center border border-gray-300 rounded-2xl shadow-lg p-8 bg-white" style={{ backgroundColor: 'white' }}>
            <h1 className="text-4xl font-bold text-black sm:text-5xl md:text-6xl relative inline-block mb-6">
              Refer Your Clients to us and Earn
            </h1>
            <p className="mt-3 max-w-3xl mx-auto text-base text-black sm:text-lg md:mt-5 md:text-xl">
              At <span className="font-bold">My Property Group</span>, we believe in building strong, long-lasting partnerships. If you know someone who could benefit from our services, we'd love to hear from you.
            </p>
          </div>
        </div>

        <div className="relative w-full" style={{ 
        backgroundImage: `url(${backgroundPattern})`, 
        backgroundRepeat: 'repeat', 
        backgroundSize: '100px 100px',
        padding: '4rem 0'
      }}>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white border-gray-300 border rounded-xl shadow-md p-8" style={{ backgroundColor: 'white' }}>
            <div className="prose max-w-none">
              <p className="text-lg mb-6">
                Whether your referral needs help with <span className="font-bold">Brokerage</span>, <span className="font-bold">Legals</span>, <span className="font-bold">Refurbishments</span>, <span className="font-bold">Lettings</span>, or would like to become a White Label Partner, we're here to provide tailored solutions to meet their unique needs.
              </p>
              
              <p className="text-lg mb-6">
                As a thank-you for your referrals, we offer attractive earning opportunities tailored to each service:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {rewardItems.map((item, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50">
                    <h3 className="font-bold text-lg mb-2">{item.service}</h3>
                    <p>{item.reward}</p>
                  </div>
                ))}
              </div>

              <p className="text-lg mb-6">
                Start earning today while helping others discover tailored property solutions. Your recommendations mean the world to us!
              </p>

              <p className="text-xl font-bold">
                Fill in the details below to share their information with us.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Referral Form Section */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-300 rounded-2xl shadow-lg p-8 flex flex-col gap-4 mt-8">
          <h2 className="text-2xl font-bold mb-4">Referral Form</h2>
          
          {submitStatus === 'success' && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
              Thank you! Your referral has been submitted successfully.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {errorMessage}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Your Name</label>
              <input
                type="text"
                name="Your Name"
                value={formData["Your Name"]}
                onChange={handleInputChange}
                className="py-2 px-3 block w-full border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral First Name</label>
              <input
                type="text"
                name="Referral First Name"
                value={formData["Referral First Name"]}
                onChange={handleInputChange}
                className="py-2 px-3 block w-full border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral Last Name</label>
              <input
                type="text"
                name="Referral Last Name"
                value={formData["Referral Last Name"]}
                onChange={handleInputChange}
                className="py-2 px-3 block w-full border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral Type</label>
              <select
                name="Referral Type"
                value={formData["Referral Type"]}
                onChange={handleInputChange}
                className="py-2 px-3 block w-full border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="">Select type</option>
                <option value="Brokerage">Brokerage</option>
                <option value="Legals">Legals</option>
                <option value="Refurbishment">Refurbishment</option>
                <option value="Lettings">Lettings</option>
                <option value="White Label Partnership">White Label Partnership</option>
                <option value="Listings">Listings</option>
                <option value="Bridging">Bridging</option>
                <option value="Housing Association Services">Housing Association Services</option>
                <option value="Furnishing">Furnishings & Interior Design</option>
                <option value="Removals">Removals</option>
                <option value="Surveying">Surveying</option>
                <option value="Digital Marketing">Digital Marketing</option>
                <option value="Company Setup">Company Setup</option>
                <option value="Planning Consultancy">Planning & Development</option>
                <option value="Insurance">Insurance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral Phone Number</label>
              <input
                type="tel"
                name="Referral Phone Number"
                value={formData["Referral Phone Number"]}
                onChange={handleInputChange}
                className="py-2 px-3 block w-full border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral Email Address</label>
              <input
                type="email"
                name="Referral Email Address"
                value={formData["Referral Email Address"]}
                onChange={handleInputChange}
                className="py-2 px-3 block w-full border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Additional Information About Your Referral</label>
            <textarea
              name="Additional Info About Your Referral (Optional)"
              value={formData["Additional Info About Your Referral (Optional)"]}
              onChange={handleInputChange}
              rows={4}
              className="py-2 px-3 block w-full border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-4 mt-4">
            <button
              type="reset"
              onClick={() => setFormData({
                "Your Name": "",
                "Referral First Name": "",
                "Referral Last Name": "",
                "Referral Type": "",
                "Referral Phone Number": "",
                "Referral Email Address": "",
                "Additional Info About Your Referral (Optional)": "",
                "Website Referred From": theme?.name || "Whitelabel Website"
              })}
              style={{
                background: isWhiteOrShade(theme?.color1) ? "black" : getThemeColor(theme?.color1),
                color: isWhiteOrShade(theme?.color1) ? "white" : getThemeColor(theme?.txtColor),
                padding: "10px",
                borderRadius: "5px",
                border: "none",
                cursor: "pointer",
                transition: "background 0.3s ease",
                boxSizing: "border-box"
              } as CSSProperties}
              onMouseOver={(e) => {
                e.currentTarget.style.background = isWhiteOrShade(theme?.color2) ? "black" : getThemeColor(theme?.color2);
                e.currentTarget.style.color = isWhiteOrShade(theme?.color1) ? "white" : getThemeColor(theme?.txtColor);
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = isWhiteOrShade(theme?.color1) ? "black" : getThemeColor(theme?.color1);
                e.currentTarget.style.color = isWhiteOrShade(theme?.color1) ? "white" : getThemeColor(theme?.txtColor);
              }}
            >
              Clear Form
            </button>
            <button
              type="submit"
              disabled={submitStatus === 'loading'}
              style={{
                background: isWhiteOrShade(theme?.color2) ? "black" : getThemeColor(theme?.color2),
                color: isWhiteOrShade(theme?.color2) ? "white" : getThemeColor(theme?.txtColor),
                padding: "10px",
                borderRadius: "5px",
                border: "none",
                cursor: submitStatus === 'loading' ? "not-allowed" : "pointer",
                transition: "background 0.3s ease",
                boxSizing: "border-box",
                opacity: submitStatus === 'loading' ? 0.7 : 1
              } as CSSProperties}
              onMouseOver={(e) => {
                if (submitStatus !== 'loading') {
                  e.currentTarget.style.background = isWhiteOrShade(theme?.color1) ? "black" : getThemeColor(theme?.color1);
                  e.currentTarget.style.color = isWhiteOrShade(theme?.color2) ? "white" : getThemeColor(theme?.txtColor);
                }
              }}
              onMouseOut={(e) => {
                if (submitStatus !== 'loading') {
                  e.currentTarget.style.background = isWhiteOrShade(theme?.color2) ? "black" : getThemeColor(theme?.color2);
                  e.currentTarget.style.color = isWhiteOrShade(theme?.color2) ? "white" : getThemeColor(theme?.txtColor);
                }
              }}
            >
              {submitStatus === 'loading' ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>
  );
};

export default ReferralPage;